import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { B } from '../constants';
import { destinations as destinationsApi, trips as tripsApi } from '../api';
import useDebouncedDestSearch from '../hooks/useDebouncedDestSearch';
import MapView from '../components/MapView';
import DestinationCard from '../components/DestinationCard';
import { ImgOrFallback } from '../components/FallbackImg';
import { directionsUrl } from '../lib/directions';
import { trackActivity } from '../lib/activityEvents';
import { getTripImageByDestination } from '../lib/tripImages';

// Manual, grounded trip planner (entered from Explore). Destination selection reuses
// the SAME search as Home / Explore / AI Planner (destinations.search + DestinationCard);
// once a destination is picked we resolve it to our KB metro (destinations.palette,
// which uses the AI planner's own resolver) and show every verified place as the
// palette. The traveller drops places onto days; we show straight-line distances,
// surface the nearest unplanned places, and — via /trips/plan-check — warn when a day
// is unrealistic, all before saving. AI auto-fill is deferred to a later phase.

const FONT = "'Montserrat',sans-serif";

// Pure great-circle distance (km). Geometry only — no transport model — so it never
// drifts from the backend; the feasibility verdict/minutes come from plan-check.
function haversineKm(a, b) {
  if (!a || !b || a.lat == null || b.lat == null) return 0;
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}
const fmtKm = (km) => (km < 1 ? `${Math.round(km * 1000)} m` : `${km.toFixed(1)} km`);
const fmtMin = (m) => (m >= 60 ? `${Math.floor(m / 60)}h${m % 60 ? ` ${m % 60}m` : ''}` : `${m} min`);

const CAT_EMOJI = {
  landmark: '🏛️', museum: '🖼️', park: '🌳', viewpoint: '🌄', religious: '⛪',
  beach: '🏖️', nightlife: '🌃', bar: '🍸', food: '🍽️', attraction: '📍',
};
const catIcon = (c) => CAT_EMOJI[c] || '📍';

const MODE_LABEL = { walk: '🚶 walk', transit: '🚇 transit', drive: '🚗 drive' };

export default function ManualPlannerScreen({ goScreen, user, showToast, onAuthRequired, initialCity }) {
  const [title, setTitle] = useState('');
  const [transport, setTransport] = useState('transit');
  const [pace, setPace] = useState('balanced');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Selected destinations (resolved KB cities) + palette per city.
  const [cities, setCities] = useState([]); // [{id,name,country,lat,lng}]
  const [palettesByCity, setPalettesByCity] = useState({}); // { [cityId]: places[] }
  const [resolving, setResolving] = useState(false);

  // Destination picker — SAME search as Home/Explore/AI Planner.
  const [destQuery, setDestQuery] = useState('');
  const { results: destResults, loading: destSearching } = useDebouncedDestSearch(destQuery, {
    enabled: destQuery.length >= 1,
    fetchEmpty: false,
  });

  // Days: [{ title, activities: [{key,id,name,lat,lng,cityId,cityName,category,durationMin,time}] }]
  const [days, setDays] = useState([{ title: '', activities: [] }]);
  const [activeDay, setActiveDay] = useState(0);

  // Palette UX
  const [catFilter, setCatFilter] = useState('all');
  const [paletteQuery, setPaletteQuery] = useState('');
  const [sortBy, setSortBy] = useState('popular'); // 'popular' | 'nearest' (to the city centre)
  const [selected, setSelected] = useState(null); // a place the user tapped → show nearest
  const [visibleCount, setVisibleCount] = useState(60); // client-side pagination of the palette
  const [remoteResults, setRemoteResults] = useState([]); // backend metro search (the tail)
  const [remoteSearching, setRemoteSearching] = useState(false);
  const searchTimer = useRef(null);

  // Feasibility (plan-check) + save
  const [check, setCheck] = useState(null);
  const [checking, setChecking] = useState(false);
  const [saving, setSaving] = useState(false);
  const checkTimer = useRef(null);

  // Ask once per signed-out visit: sign-in opens over this screen, and re-asking on every
  // render would reopen it the moment it is dismissed.
  const onAuthRequiredRef = useRef(onAuthRequired);
  onAuthRequiredRef.current = onAuthRequired;
  useEffect(() => {
    if (!user && onAuthRequiredRef.current) onAuthRequiredRef.current();
  }, [user]);

  // Prefill: a search term seeds the destination search; a resolved city
  // ({ cityId|placeId|lat/lng, name } from DetailScreen) is added directly via the
  // same addDestination → palette path, skipping the search step.
  const prefilledCity = useRef(null);
  useEffect(() => {
    if (!initialCity) return;
    if (typeof initialCity.prefillQuery === 'string' && initialCity.prefillQuery) {
      setDestQuery(initialCity.prefillQuery);
      return;
    }
    const { name, cityId, placeId, lat, lng } = initialCity;
    const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);
    if (!name || (cityId == null && !placeId && !hasCoords)) return;
    if (prefilledCity.current === initialCity) return;
    prefilledCity.current = initialCity;
    const { addPlace } = initialCity;
    Promise.resolve(addDestinationRef.current({
      name,
      placeId: placeId || (cityId != null ? `gb:city:${cityId}` : undefined),
      lat: hasCoords ? lat : undefined,
      lng: hasCoords ? lng : undefined,
    })).then((city) => {
      // "+ Add to trip" from a city page: put that place straight on Day 1.
      if (!city || !addPlace || addPlace.id == null) return;
      addToDayRef.current({ ...addPlace, cityId: city.id, cityName: city.name }, 0);
      showToastRef.current && showToastRef.current(`${addPlace.name} added to Day 1`);
    });
  }, [initialCity]);

  // Edit mode (from My Trips): { editTripId } loads the saved trip's days and stops,
  // reloads the palettes of its cities, and saves back with trips.update.
  const editTripId = initialCity && initialCity.editTripId ? initialCity.editTripId : null;
  const loadedEditTrip = useRef(null);
  useEffect(() => {
    if (!editTripId || loadedEditTrip.current === editTripId) return;
    loadedEditTrip.current = editTripId;
    (async () => {
      setResolving(true);
      try {
        const trip = await tripsApi.get(editTripId);
        setTitle(trip.title || '');
        const fmtDateInput = (d) => {
          if (!d) return '';
          if (typeof d === 'string') return d.slice(0, 10);
          try {
            const x = new Date(d);
            return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
          } catch {
            return '';
          }
        };
        if (trip.startDate) setStartDate(fmtDateInput(trip.startDate));
        if (trip.endDate) setEndDate(fmtDateInput(trip.endDate));
        const savedDays = (trip.days || []).map((d, di) => ({
          title: d.title || '',
          activities: (d.activities || [])
            .slice()
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map((a, ai) => ({
              key: `saved-${a.id}-${di}-${ai}`,
              id: `saved-${a.id}`, name: a.name, lat: a.lat, lng: a.lng,
              cityId: a.cityId, cityName: a.location || '', category: null,
              durationMin: null, time: a.time || '', notes: a.notes || undefined, placeId: a.placeId || null,
            })),
        }));
        const cityIds = [...new Set(savedDays.flatMap((d) => d.activities.map((a) => a.cityId)).filter((id) => id != null))];
        const loadedCities = [];
        const loadedPalettes = {};
        for (const cityId of cityIds) {
          try {
            const { city, places, hasMore, total } = await destinationsApi.palette({ cityId, page: 0 });
            if (!city || !(places && places.length) || loadedCities.some((c) => c.id === city.id)) continue;
            loadedCities.push(city);
            loadedPalettes[city.id] = { city, page: 0, hasMore: !!hasMore, total: total || 0, places: places.map((p) => ({ ...p, cityId: city.id, cityName: city.name })) };
          } catch { /* keep the saved stops even if a palette fails to load */ }
        }
        // Re-link saved stops to their palette places so they show as already added.
        const daysWithPlaces = savedDays.map((d) => ({
          ...d,
          activities: d.activities.map((a) => {
            const pal = loadedPalettes[a.cityId];
            const match = pal && pal.places.find((p) => p.name === a.name);
            return match
              ? { ...a, id: match.id, cityName: match.cityName, category: match.category, durationMin: match.durationMin || null }
              : a;
          }),
        }));
        setCities(loadedCities);
        setPalettesByCity(loadedPalettes);
        setDays(daysWithPlaces.length ? daysWithPlaces : [{ title: '', activities: [] }]);
        setActiveDay(0);
      } catch (err) {
        showToast && showToast(err.message || 'Failed to load trip', 'error');
      } finally {
        setResolving(false);
      }
    })();
  }, [editTripId, showToast]);

  const closePicker = () => setDestQuery('');

  // ── Add a destination: resolve to our KB metro, load its palette ──────
  const addDestination = async (dest) => {
    if (!dest || !dest.name) return;
    setResolving(true);
    try {
      const { city, places, hasMore, total } = await destinationsApi.palette({ cityId: dest.cityId, name: dest.name, placeId: dest.placeId, lat: dest.lat, lng: dest.lng, page: 0 });
      if (!city || !(places && places.length)) {
        showToast && showToast(`We don't have detailed attraction data for ${dest.name} yet`, 'error');
        return;
      }
      if (cities.some((c) => c.id === city.id)) { closePicker(); return city; }
      const next = [...cities, city];
      setCities(next);
      setPalettesByCity((m) => ({
        ...m,
        [city.id]: { city, page: 0, hasMore: !!hasMore, total: total || 0, places: places.map((p) => ({ ...p, cityId: city.id, cityName: city.name })) },
      }));
      if (!title) setTitle(next.length === 1 ? `Trip to ${city.name}` : `${next.map((c) => c.name).join(' & ')} trip`);
      closePicker();
      return city;
    } catch {
      showToast && showToast('Failed to load that destination', 'error');
    } finally {
      setResolving(false);
    }
  };

  const addDestinationRef = useRef(addDestination);
  addDestinationRef.current = addDestination;

  const removeCity = (cityId) => {
    setCities((cs) => cs.filter((c) => c.id !== cityId));
    setPalettesByCity((m) => { const n = { ...m }; delete n[cityId]; return n; });
    // Any already-planned stops from that city stay (the user's choice).
  };

  // ── Day trips from the base city (the FIRST added destination) ────────
  const [dayTrips, setDayTrips] = useState([]); // [{ id,title,icon,travelTime,plannable,cityId,cityName,lat,lng }]
  const homeCityId = cities.length ? cities[0].id : null;
  useEffect(() => {
    if (homeCityId == null) { setDayTrips([]); return undefined; }
    setDayTrips([]);
    let cancelled = false;
    destinationsApi.daytrips(homeCityId)
      .then((r) => { if (!cancelled) setDayTrips((r && r.dayTrips) || []); })
      .catch(() => { if (!cancelled) setDayTrips([]); });
    return () => { cancelled = true; };
  }, [homeCityId]);

  // Trips whose target city is already a destination chip are hidden; removing
  // the chip brings them back. Info-only trips (no cityId) always stay.
  const visibleDayTrips = useMemo(
    () => (homeCityId == null ? [] : dayTrips.filter((dt) => dt.cityId == null || !cities.some((c) => c.id === dt.cityId))),
    [dayTrips, cities, homeCityId],
  );

  // "Plan this day": behaves exactly like the user had searched and picked the
  // target city — same addDestination path → KB resolve → palette + per-day add buttons.
  const planDayTrip = (dt) => {
    if (!dt.plannable || dt.cityId == null || !dt.cityName) return;
    addDestination({ name: dt.cityName, placeId: `gb:city:${dt.cityId}`, lat: dt.lat, lng: dt.lng });
  };

  // "Just add": append a titled day. Plannable trips carry one synthetic stop so
  // the day has a location (map/feasibility work); info-only days are title-only.
  // Attraction trips (a single in-home-city Place) get the same synthetic stop
  // from the place coords.
  const justAddDayTrip = (dt) => setDays((d) => {
    const di = d.length;
    const isAttraction = dt.targetPlaceId != null;
    const addable = dt.plannable && (dt.cityId != null || isAttraction);
    return [...d, {
      title: dt.title,
      activities: addable ? [{
        key: `daytrip-${dt.id}-${di}-0-${Date.now()}`,
        id: isAttraction ? `daytrip-place-${dt.targetPlaceId}` : `daytrip-${dt.id}`,
        name: dt.title,
        lat: dt.lat, lng: dt.lng,
        cityId: dt.cityId, cityName: dt.cityName,
        category: null, durationMin: null, time: '',
      }] : [],
    }];
  });

  const palette = useMemo(
    () => Object.values(palettesByCity).flatMap((e) => (e && e.places) || []),
    [palettesByCity]
  );
  const anyHasMore = useMemo(
    () => Object.values(palettesByCity).some((e) => e && e.hasMore),
    [palettesByCity]
  );
  const [loadingMore, setLoadingMore] = useState(false);

  // Pull every remaining page of places from each destination's metro (server-side
  // pagination), appending each page as it arrives, deduped against what's loaded.
  const loadAllPages = async () => {
    const entries = Object.values(palettesByCity).filter((e) => e && e.hasMore);
    if (entries.length === 0) return;
    setLoadingMore(true);
    try {
      await Promise.all(entries.map(async (e) => {
        for (let page = e.page + 1; ; page += 1) {
          const r = await destinationsApi.palette({ cityId: e.city.id, lat: e.city.lat, lng: e.city.lng, page }).catch(() => null);
          if (!r) return;
          const incoming = (r.places || []).map((p) => ({ ...p, cityId: e.city.id, cityName: e.city.name }));
          setPalettesByCity((m) => {
            const prev = m[e.city.id];
            if (!prev) return m;
            const ids = new Set(prev.places.map((p) => p.id));
            const names = new Set(prev.places.map((p) => p.name.trim().toLowerCase()));
            const fresh = incoming.filter((p) => !ids.has(p.id) && !names.has(p.name.trim().toLowerCase()));
            return { ...m, [e.city.id]: { ...prev, page, hasMore: !!r.hasMore, places: [...prev.places, ...fresh] } };
          });
          if (!r.hasMore) return;
        }
      }));
    } finally {
      setLoadingMore(false);
    }
  };

  // ── Day / stop mutations ──────────────────────────────────
  const addDay = () => setDays((d) => [...d, { title: '', activities: [] }]);
  const removeDay = (di) => setDays((d) => {
    const next = d.filter((_, i) => i !== di);
    return next.length ? next : [{ title: '', activities: [] }];
  });

  const isPlanned = useCallback(
    (placeId) => days.some((d) => d.activities.some((a) => a.id === placeId)),
    [days]
  );
  const inDay = useCallback(
    (placeId, di) => !!(days[di] && days[di].activities.some((a) => a.id === placeId)),
    [days]
  );

  const addToDay = (place, di = activeDay) => {
    setDays((d) => d.map((day, i) => {
      if (i !== di) return day;
      if (day.activities.some((a) => a.id === place.id)) return day;
      const activity = {
        key: `${place.id}-${i}-${day.activities.length}`,
        id: place.id, name: place.name, lat: place.lat, lng: place.lng,
        cityId: place.cityId, cityName: place.cityName, category: place.category,
        durationMin: place.durationMin || null, time: '',
      };
      return { ...day, activities: [...day.activities, activity] };
    }));
  };
  const addToDayRef = useRef(addToDay);
  addToDayRef.current = addToDay;
  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;
  const cardRefs = useRef({});
  // Tapping a browse pin on the map selects that place (highlights its card, nearest
  // panel) without scrolling the page away from the map.
  const focusFromMap = (m) => {
    const p = m && m.source && m.source.place;
    if (!p) return;
    setSelected(p);
  };

  const removeStop = (di, key) => setDays((d) => d.map((day, i) =>
    i === di ? { ...day, activities: day.activities.filter((a) => a.key !== key) } : day));

  const setStopTime = (di, key, time) => setDays((d) => d.map((day, i) =>
    i === di ? { ...day, activities: day.activities.map((a) => (a.key === key ? { ...a, time } : a)) } : day));

  const moveStopToDay = (fromDi, key, toDi) => setDays((d) => {
    if (fromDi === toDi) return d;
    const stop = d[fromDi].activities.find((a) => a.key === key);
    if (!stop) return d;
    return d.map((day, i) => {
      if (i === fromDi) return { ...day, activities: day.activities.filter((a) => a.key !== key) };
      if (i === toDi) return { ...day, activities: [...day.activities, stop] };
      return day;
    });
  });

  const reorderStop = (di, idx, dir) => setDays((d) => d.map((day, i) => {
    if (i !== di) return day;
    const arr = [...day.activities];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return day;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    return { ...day, activities: arr };
  }));

  const applySuggestedOrder = (di, order) => setDays((d) => d.map((day, i) => {
    if (i !== di) return day;
    return { ...day, activities: order.map((oi) => day.activities[oi]).filter(Boolean) };
  }));

  // ── Feasibility check (debounced on any day change) ───────
  useEffect(() => {
    const hasStops = days.some((d) => d.activities.length > 0);
    if (!hasStops) { setCheck(null); return undefined; }
    if (checkTimer.current) clearTimeout(checkTimer.current);
    checkTimer.current = setTimeout(() => {
      setChecking(true);
      tripsApi.planCheck({
        transport, pace,
        days: days.map((d, i) => ({
          dayNumber: i + 1,
          stops: d.activities.map((a) => ({
            name: a.name, lat: a.lat, lng: a.lng, cityId: a.cityId,
            durationMin: a.durationMin, category: a.category,
          })),
        })),
      })
        .then(setCheck)
        .catch(() => setCheck(null))
        .finally(() => setChecking(false));
    }, 450);
    return () => checkTimer.current && clearTimeout(checkTimer.current);
  }, [days, transport, pace]);

  const dayCheck = (di) => (check && check.days ? check.days.find((d) => d.dayNumber === di + 1) : null);

  // Reset pagination whenever the filter/query/palette changes.
  useEffect(() => { setVisibleCount(60); }, [paletteQuery, catFilter, palette.length]);
  // "Show all places" keeps every loaded page visible while the rest stream in.
  const [showAll, setShowAll] = useState(false);
  const cityKey = Object.keys(palettesByCity).join(',');
  useEffect(() => { setShowAll(false); }, [paletteQuery, catFilter, cityKey]);
  const shownCount = showAll ? Infinity : visibleCount;

  // Hybrid search: filter the loaded palette client-side; only when it has NO name
  // match do we hit the backend to search the city's full metro (the long tail).
  useEffect(() => {
    const q = paletteQuery.trim();
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (q.length < 2 || cities.length === 0) { setRemoteResults([]); return undefined; }
    const localMatch = palette.some((p) => p.name.toLowerCase().includes(q.toLowerCase()));
    if (localMatch) { setRemoteResults([]); return undefined; }
    searchTimer.current = setTimeout(async () => {
      setRemoteSearching(true);
      try {
        const perCity = await Promise.all(cities.map((c) =>
          destinationsApi.paletteSearch(c.id, q)
            .then((r) => (r.places || []).map((p) => ({ ...p, cityId: c.id, cityName: c.name })))
            .catch(() => [])
        ));
        const paletteIds = new Set(palette.map((p) => p.id));
        const seen = new Set();
        const merged = [];
        for (const p of perCity.flat()) {
          if (paletteIds.has(p.id) || seen.has(p.id)) continue;
          seen.add(p.id);
          merged.push(p);
        }
        setRemoteResults(merged);
      } catch {
        setRemoteResults([]);
      } finally {
        setRemoteSearching(false);
      }
    }, 400);
    return () => searchTimer.current && clearTimeout(searchTimer.current);
  }, [paletteQuery, palette, cities]);

  // ── Palette derived views ─────────────────────────────────
  const categories = useMemo(() => {
    const set = new Set(palette.map((p) => p.category).filter(Boolean));
    return ['all', ...[...set].sort()];
  }, [palette]);

  const filteredPalette = useMemo(() => {
    const q = paletteQuery.trim().toLowerCase();
    const list = palette.filter((p) =>
      (catFilter === 'all' || p.category === catFilter) &&
      (!q || p.name.toLowerCase().includes(q))
    );
    return sortBy === 'nearest'
      ? list.slice().sort((a, b) => (a.distanceKm ?? Infinity) - (b.distanceKm ?? Infinity))
      : list;
  }, [palette, catFilter, paletteQuery, sortBy]);

  // Nearest unplanned places to the selected one (for "highlight nearest").
  const nearest = useMemo(() => {
    if (!selected) return [];
    return palette
      .filter((p) => p.id !== selected.id && !isPlanned(p.id))
      .map((p) => ({ ...p, _km: haversineKm(selected, p) }))
      .sort((a, b) => a._km - b._km)
      .slice(0, 6);
  }, [selected, palette, isPlanned]);

  // ── Map locations: planned stops (day-numbered) + the places being browsed (dots),
  // so the spread of the city is visible before anything is picked.
  const mapLocations = useMemo(() => {
    const imageById = new Map(palette.map((p) => [p.id, p.imageUrl]));
    const planned = days.flatMap((d, i) => d.activities.map((a) => ({
      id: `stop-${a.key || a.id}-${i}`, lat: a.lat, lng: a.lng, name: a.name, dayNumber: i + 1,
      imageUrl: imageById.get(a.id) || '',
    })));
    const plannedIds = new Set(days.flatMap((d) => d.activities.map((a) => a.id)));
    const browse = filteredPalette.slice(0, Math.min(shownCount, 120))
      .filter((p) => !plannedIds.has(p.id))
      .map((p) => ({
        id: `pin-${p.cityId}-${p.id}`, lat: p.lat, lng: p.lng, name: p.name, pin: true, place: p, imageUrl: p.imageUrl,
        description: [p.category, p.distanceKm != null ? `${fmtKm(p.distanceKm)} from ${p.cityName} centre` : null].filter(Boolean).join(' · '),
        // Planned places leave the browse pins, so every day here is still addable.
        actions: days.map((_, di) => ({
          label: `+D${di + 1}`, ariaLabel: `Add ${p.name} to Day ${di + 1}`, onClick: () => addToDayRef.current(p, di),
        })),
      }));
    return [...planned, ...browse];
  }, [days, filteredPalette, shownCount, palette]);

  const totalStops = days.reduce((n, d) => n + d.activities.length, 0);

  // ── Save ──────────────────────────────────────────────────
  const handleSave = async () => {
    if (!user) { onAuthRequired && onAuthRequired(); return; }
    if (totalStops === 0) { showToast && showToast('Add at least one attraction first', 'error'); return; }
    setSaving(true);
    try {
      const destinationStr = cities.map((c) => c.name).join(', ') || 'My trip';
      const addDays = (dateStr, daysToAdd) => {
        if (!dateStr) return null;
        const d = new Date(dateStr);
        if (isNaN(d.getTime())) return null;
        d.setDate(d.getDate() + daysToAdd);
        return d.toISOString().slice(0, 10);
      };
      const payload = {
        source: 'MANUAL',
        title: title || (cities[0] ? `Trip to ${cities[0].name}` : 'My trip'),
        destination: destinationStr,
        coverImage: getTripImageByDestination(destinationStr || (cities[0] && cities[0].name) || ''),
        ...(startDate ? { startDate } : {}),
        ...(endDate ? { endDate } : {}),
        // Filter out empty days BEFORE numbering so the saved trip has no day-number
        // gaps (e.g. leaving Day 2 empty must not save days numbered 1, 3).
        days: days
          .filter((d) => d.activities.length > 0)
          .map((d, i) => ({
            dayNumber: i + 1,
            title: d.title || '',
            ...(startDate ? { date: addDays(startDate, i) } : {}),
            activities: d.activities.map((a) => ({
              name: a.name,
              time: a.time || '',
              location: a.cityName || '',
              lat: a.lat, lng: a.lng, cityId: a.cityId || null,
              ...(a.notes ? { notes: a.notes } : {}),
              ...(a.placeId ? { placeId: a.placeId } : {}),
            })),
          })),
      };
      const trip = editTripId
        ? await tripsApi.update(editTripId, payload)
        : await tripsApi.create(payload);
      trackActivity('planner', 'trip_plan_generated', { mode: 'manual', tripId: trip?.id ?? null });
      showToast && showToast(editTripId ? 'Trip updated' : 'Trip saved! 🎉');
      goScreen('tripDetail', trip);
    } catch (err) {
      showToast && showToast(err.message || 'Failed to save trip', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Styles ────────────────────────────────────────────────
  const card = { background: '#fff', border: '1px solid #eee', borderRadius: 12, padding: 12 };
  const pill = (active) => ({
    padding: '6px 12px', borderRadius: 20, border: `1px solid ${active ? B.forest : '#ddd'}`,
    background: active ? B.forest : '#fff', color: active ? '#fff' : B.night,
    fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: FONT, whiteSpace: 'nowrap',
  });

  // A palette attraction card — reused for the loaded palette and backend search hits.
  const renderCard = (p) => (
    <div key={`${p.cityId}-${p.id}`}
      ref={(el) => { const k = `${p.cityId}-${p.id}`; if (el) cardRefs.current[k] = el; else delete cardRefs.current[k]; }}
      style={{ ...card, display: 'flex', gap: 10, alignItems: 'center', ...(selected && selected.id === p.id && selected.cityId === p.cityId ? { borderColor: B.forest, background: '#f1f8f4' } : null) }}>
      <div
        onClick={() => setSelected(p)}
        style={{ width: 40, height: 40, borderRadius: 8, background: B.sand, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, cursor: 'pointer', overflow: 'hidden' }}
      >
        <ImgOrFallback src={p.imageUrl} fallback={catIcon(p.category)} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
      </div>
      <div style={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={() => setSelected(p)}>
        <div style={{ fontSize: 13, fontWeight: 600, color: B.night, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
        <div style={{ fontSize: 11, color: B.muted }}>
          {catIcon(p.category)} {p.category}{p.durationMin ? ` · ⏱ ${fmtMin(p.durationMin)} visit` : ''}
          {p.distanceKm != null ? ` · ${fmtKm(p.distanceKm)} from ${p.cityName} centre` : (cities.length > 1 ? ` · ${p.cityName}` : '')}
        </div>
      </div>
      {/* One button per day → add this attraction directly to any day. */}
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end', maxWidth: 132 }}>
        {days.map((_, di) => {
          const added = inDay(p.id, di);
          return (
            <button
              key={di} onClick={() => addToDay(p, di)} disabled={added}
              title={added ? `Already in Day ${di + 1}` : `Add to Day ${di + 1}`}
              style={{ ...pill(false), padding: '4px 8px', fontSize: 11, color: added ? B.muted : B.forest, borderColor: added ? '#eee' : B.forest, cursor: added ? 'default' : 'pointer' }}
            >
              {added ? `✓D${di + 1}` : `+D${di + 1}`}
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '20px 16px 80px', fontFamily: FONT }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
        <button onClick={() => goScreen(editTripId ? 'trips' : 'home')} aria-label={editTripId ? 'Back to My Trips' : 'Back'} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20, color: B.muted }}>←</button>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: B.night, margin: 0, flex: 1, minWidth: 180 }}>{editTripId ? 'Edit trip' : 'Plan your trip'}</h1>
        <button
          onClick={handleSave}
          disabled={saving || totalStops === 0}
          style={{
            padding: '10px 20px', background: totalStops === 0 ? '#ccc' : B.forest, color: '#fff',
            border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700,
            cursor: totalStops === 0 ? 'not-allowed' : 'pointer', fontFamily: FONT,
          }}
        >
          {saving ? 'Saving…' : `${editTripId ? 'Save changes' : 'Save trip'}${totalStops ? ` (${totalStops})` : ''}`}
        </button>
      </div>

      {/* Trip title + transport + pace + dates */}
      <div style={{ ...card, marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Trip title"
          style={{ flex: 1, minWidth: 160, padding: '10px 12px', border: '1px solid #ddd', borderRadius: 8, fontSize: 14, fontFamily: FONT }}
        />
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: B.muted, fontWeight: 600 }}>Getting around:</span>
          {['foot', 'transit', 'car'].map((t) => (
            <button key={t} onClick={() => setTransport(t)} style={pill(transport === t)}>
              {t === 'foot' ? '🚶 Walk' : t === 'transit' ? '🚇 Transit' : '🚗 Car'}
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: B.muted, fontWeight: 600 }}>Pace:</span>
          {['relaxed', 'balanced', 'packed'].map((p) => (
            <button key={p} onClick={() => setPace(p)} style={pill(pace === p)}>{p}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 12, color: B.muted, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
            Start date
            <input
              type="date"
              min={new Date().toISOString().split('T')[0]}
              value={startDate}
              onChange={(e) => {
                const val = e.target.value;
                setStartDate(val);
                if (endDate && val && endDate < val) setEndDate('');
              }}
              style={{ padding: '6px 8px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13, fontFamily: FONT }}
            />
          </label>
          <label style={{ fontSize: 12, color: B.muted, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
            End date
            <input
              type="date"
              min={startDate || new Date().toISOString().split('T')[0]}
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{ padding: '6px 8px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13, fontFamily: FONT }}
            />
          </label>
          {startDate && endDate && (() => {
            const sD = new Date(startDate);
            const eD = new Date(endDate);
            if (eD < sD) return null;
            const diff = Math.round((eD - sD) / 86400000) + 1;
            if (diff <= 0) return null;
            return <span style={{ fontSize: 12, color: B.muted }}>📅 {diff} days trip</span>;
          })()}
        </div>
      </div>

      {/* Destination chips + always-visible search */}
      {(cities.length > 0 || resolving) && (
      <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {cities.map((c) => (
          <span key={c.id} style={{ ...pill(true), display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            📍 {c.name} <span style={{ opacity: 0.7 }}>({((palettesByCity[c.id] || {}).places || []).length}{(palettesByCity[c.id] || {}).hasMore ? '+' : ''})</span>
            <span onClick={() => removeCity(c.id)} style={{ cursor: 'pointer', marginLeft: 2 }}>✕</span>
          </span>
        ))}
        {resolving && <span style={{ fontSize: 12, color: B.muted }}>Loading places…</span>}
      </div>
      )}

      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fff', border: '1.5px solid #e7e2d9', borderRadius: 50, padding: '10px 18px', maxWidth: 520 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={B.muted} strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
          <input
            value={destQuery} onChange={(e) => setDestQuery(e.target.value)}
            placeholder={cities.length ? 'Add another destination…' : 'Search any destination…'}
            aria-label="Search destinations"
            style={{ border: 'none', fontSize: 14, outline: 'none', width: '100%', fontFamily: FONT, background: 'transparent' }}
          />
          {destSearching && <span style={{ color: B.muted, fontSize: 12 }}>…</span>}
        </div>
        {destQuery.length >= 1 && destResults.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(200px,1fr))', gap: 14, marginTop: 14 }}>
            {destResults.map((d) => (
              <DestinationCard key={d.id || d.placeId} destination={d} onClick={() => addDestination(d)} showPartners={false} />
            ))}
          </div>
        )}
        {destQuery.length >= 1 && !destSearching && destResults.length === 0 && (
          <div style={{ color: B.muted, fontSize: 13, padding: '10px 2px' }}>No destinations found for “{destQuery}”.</div>
        )}
      </div>

      {cities.length === 0 && !(editTripId && totalStops > 0) ? (
        <div style={{ ...card, textAlign: 'center', padding: 40, color: B.muted }}>
          <div style={{ fontSize: 40, marginBottom: 8 }}>🗺️</div>
          <div style={{ fontWeight: 700, color: B.night, marginBottom: 4 }}>Start by adding a destination</div>
          <div style={{ fontSize: 14 }}>Search a place above and we'll show every attraction we have — then build your days.</div>
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* ── LEFT: palette ── */}
          <div style={{ flex: '1 1 340px', minWidth: 300 }}>
            <div style={{ ...card, marginBottom: 12 }}>
              <input
                value={paletteQuery} onChange={(e) => setPaletteQuery(e.target.value)} placeholder="Search attractions…"
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 8, fontSize: 13, fontFamily: FONT, boxSizing: 'border-box', marginBottom: 8 }}
              />
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {categories.map((c) => (
                  <button key={c} onClick={() => setCatFilter(c)} style={{ ...pill(catFilter === c), padding: '4px 10px' }}>
                    {c === 'all' ? 'All' : `${catIcon(c)} ${c}`}
                  </button>
                ))}
              </div>
            </div>


            {palette.length > 0 && (
              <div style={{ fontSize: 11, color: B.muted, margin: '0 2px 6px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 4 }}>
                <span>
                Showing {Math.min(shownCount, filteredPalette.length)} of {palette.length} loaded
                {(() => {
                  const total = Object.values(palettesByCity).reduce((n, e) => n + ((e && e.total) || 0), 0);
                  return (anyHasMore && total > palette.length) ? ` · ~${total} in our data` : '';
                })()}
                </span>
                <span style={{ display: 'inline-flex', gap: 4 }}>
                  {[['popular', 'Most popular'], ['nearest', 'Closest to centre']].map(([k, label]) => (
                    <button key={k} type="button" onClick={() => setSortBy(k)} aria-pressed={sortBy === k}
                      style={{ ...pill(sortBy === k), padding: '2px 8px', fontSize: 11 }}>{label}</button>
                  ))}
                </span>
              </div>
            )}
            {palette.length > 0 && (
              <div style={{ borderRadius: 12, overflow: 'hidden', margin: '6px 0 10px', clear: 'both', width: '100%' }}>
                <MapView locations={mapLocations} height={260} onActivityHover={focusFromMap}
                  selectedId={selected ? `pin-${selected.cityId}-${selected.id}` : null} />
                <div style={{ fontSize: 11, color: B.muted, marginTop: 4 }}>
                  ● Places in the list (tap one to add it to a day) · D1, D2… stops in your days. Visit time is how long people typically spend there.
                </div>
              </div>
            )}
            {/* Nearest-to-selected panel */}
            {selected && nearest.length > 0 && (
              <div style={{ ...card, marginBottom: 12, background: '#FFFBEF', border: '1px solid #F0E4C3' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#8a6d1f', marginBottom: 8 }}>
                  Nearest to “{selected.name}”
                  <span onClick={() => setSelected(null)} style={{ float: 'right', cursor: 'pointer', color: B.muted }}>✕</span>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {nearest.map((p) => (
                    <button key={p.id} onClick={() => addToDay(p)} style={{ ...pill(false), padding: '5px 10px' }} title={`Add ${p.name}`}>
                      {catIcon(p.category)} {p.name} · {fmtKm(p._km)} +
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div className="mp-palette" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {resolving && palette.length === 0 && <div style={{ color: B.muted, fontSize: 13, padding: 8 }}>Loading places…</div>}
              {filteredPalette.slice(0, shownCount).map(renderCard)}
              {(filteredPalette.length > shownCount || anyHasMore) && (
                <button
                  onClick={() => { setShowAll(true); loadAllPages(); }}
                  disabled={loadingMore}
                  style={{ ...pill(false), width: '100%', padding: 8 }}
                >
                  {loadingMore ? `Loading places… (${palette.length} loaded)`
                    : anyHasMore ? 'Show all places'
                      : `Show more (${filteredPalette.length - shownCount})`}
                </button>
              )}
              {/* Long tail: nothing loaded matches → results from the full-metro search */}
              {!resolving && filteredPalette.length === 0 && (
                remoteSearching ? (
                  <div style={{ color: B.muted, fontSize: 13, padding: 8 }}>Searching all attractions…</div>
                ) : remoteResults.length > 0 ? (
                  <>
                    <div style={{ fontSize: 11, color: B.muted, padding: '4px 2px' }}>Beyond the top list — found by search:</div>
                    {remoteResults.map(renderCard)}
                  </>
                ) : (
                  <div style={{ color: B.muted, fontSize: 13, padding: 8 }}>
                    {paletteQuery.trim().length >= 2 ? `No places found for “${paletteQuery.trim()}”.` : 'No attractions match that filter.'}
                  </div>
                )
              )}
            </div>
          </div>

          {/* ── RIGHT: days + map ── */}
          <div style={{ flex: '1 1 420px', minWidth: 320 }}>
            {checking && (
              <div style={{ fontSize: 12, color: B.muted, marginBottom: 8, textAlign: 'right' }}>Checking feasibility…</div>
            )}
            {check && !check.overall.feasible && (
              <div style={{ ...card, marginBottom: 12, background: '#FDECEC', border: '1px solid #F5C2C2', color: '#a12', fontSize: 13, fontWeight: 600 }}>
                ⚠ Some days may be unrealistic — see the warnings below.
              </div>
            )}

            {days.map((day, di) => {
              const dc = dayCheck(di);
              return (
                <div key={di} style={{ ...card, marginBottom: 12, border: activeDay === di ? `2px solid ${B.forest}` : '1px solid #eee' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <button onClick={() => setActiveDay(di)} style={{ ...pill(activeDay === di), padding: '4px 10px' }}>Day {di + 1}</button>
                    <input
                      value={day.title}
                      onChange={(e) => setDays((d) => d.map((dd, i) => (i === di ? { ...dd, title: e.target.value } : dd)))}
                      placeholder="Day title (optional)"
                      style={{ flex: 1, padding: '6px 8px', border: '1px solid #eee', borderRadius: 6, fontSize: 13, fontFamily: FONT }}
                    />
                    {dc && dc.stopsCount > 0 && (
                      <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 8px', borderRadius: 12, color: dc.feasible ? B.forest : '#a12', background: dc.feasible ? '#e9f5ef' : '#FDECEC' }}>
                        {dc.feasible ? '✓ Realistic' : '⚠ Check'} · {fmtMin(dc.totalMinutes)}
                      </span>
                    )}
                    {days.length > 1 && <button onClick={() => removeDay(di)} style={{ background: 'none', border: 'none', color: '#c33', cursor: 'pointer', fontSize: 16 }}>🗑</button>}
                  </div>

                  {day.activities.length === 0 ? (
                    <div style={{ color: B.muted, fontSize: 13, padding: '8px 4px' }}>
                      Empty — add attractions from the left with the <b>+D{di + 1}</b> button.
                    </div>
                  ) : day.activities.map((a, idx) => {
                    const nextStop = day.activities[idx + 1];
                    const legKm = nextStop ? haversineKm(a, nextStop) : 0;
                    return (
                      <div key={a.key}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0' }}>
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <button onClick={() => reorderStop(di, idx, -1)} disabled={idx === 0} style={{ background: 'none', border: 'none', cursor: idx === 0 ? 'default' : 'pointer', color: idx === 0 ? '#ddd' : B.muted, fontSize: 10, padding: 0, lineHeight: 1 }}>▲</button>
                            <button onClick={() => reorderStop(di, idx, 1)} disabled={idx === day.activities.length - 1} style={{ background: 'none', border: 'none', cursor: idx === day.activities.length - 1 ? 'default' : 'pointer', color: idx === day.activities.length - 1 ? '#ddd' : B.muted, fontSize: 10, padding: 0, lineHeight: 1 }}>▼</button>
                          </div>
                          <input
                            value={a.time} onChange={(e) => setStopTime(di, a.key, e.target.value)} placeholder="—:—"
                            style={{ width: 58, padding: '5px 6px', border: '1px solid #eee', borderRadius: 6, fontSize: 12, fontFamily: FONT, textAlign: 'center' }}
                          />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 13, fontWeight: 600, color: B.night, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{catIcon(a.category)} {a.name}</div>
                          </div>
                          <select
                            value={di} onChange={(e) => moveStopToDay(di, a.key, Number(e.target.value))}
                            title="Move to day"
                            style={{ border: '1px solid #eee', borderRadius: 6, fontSize: 11, padding: '3px', fontFamily: FONT, color: B.muted }}
                          >
                            {days.map((_, dj) => <option key={dj} value={dj}>D{dj + 1}</option>)}
                          </select>
                          <button onClick={() => setSelected(a)} title="Show nearby" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13 }}>🧭</button>
                          <button onClick={() => removeStop(di, a.key)} style={{ background: 'none', border: 'none', color: '#c33', cursor: 'pointer', fontSize: 14 }}>✕</button>
                        </div>
                        {nextStop && (() => {
                          const leg = dc && dc.legs && dc.legs[idx];
                          return (
                            <div style={{ fontSize: 11, color: legKm > 25 ? '#c33' : B.muted, paddingLeft: 74 }}>
                              ↓ {fmtKm(legKm)}{leg ? ` · ~${fmtMin(leg.minutes)} ${MODE_LABEL[leg.mode] || leg.mode}` : ''} to next{legKm > 25 ? ' · long hop' : ''}
                              {directionsUrl(a, nextStop, transport) && (
                                <a href={directionsUrl(a, nextStop, transport)} target="_blank" rel="noopener noreferrer"
                                  aria-label={`Directions from ${a.name} to ${nextStop.name}`}
                                  style={{ marginLeft: 6, color: B.forest, fontWeight: 600, textDecoration: 'none' }}>Directions ↗</a>
                              )}
                            </div>
                          );
                        })()}
                      </div>
                    );
                  })}

                  {dc && dc.suggestedOrder && (
                    <button onClick={() => applySuggestedOrder(di, dc.suggestedOrder.order)} style={{ ...pill(false), marginTop: 8, color: B.forest, borderColor: B.forest }}>
                      ↔ Optimize order (save ~{fmtMin(dc.suggestedOrder.savesMinutes)})
                    </button>
                  )}
                  {dc && dc.warnings && dc.warnings.map((w, wi) => (
                    <div key={wi} style={{ fontSize: 12, color: '#a12', background: '#FDECEC', borderRadius: 6, padding: '6px 8px', marginTop: 8 }}>⚠ {w.message}</div>
                  ))}
                </div>
              );
            })}

            <button onClick={addDay} style={{ ...pill(false), width: '100%', padding: 10, border: '1px dashed #ccc', marginBottom: 16 }}>+ Add day</button>

            {/* Day trips from the base city (soft green panel; mockup: docs/mockups/daytrips-planner.html) */}
            {visibleDayTrips.length > 0 && (
              <div style={{ ...card, marginBottom: 16, background: 'linear-gradient(180deg,#fff,#f3fbf6)', border: '1.5px solid #cfe7d9', borderRadius: 16, padding: '14px 16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 800, fontSize: 13.5, color: B.forest, marginBottom: 3 }}>
                  🧳 Day trips from {cities[0].name}
                </div>
                <div style={{ fontSize: 11.5, color: B.muted, marginBottom: 10 }}>
                  One-day outings from {cities[0].name} — plan the stops, or just book the day as-is.
                </div>
                {visibleDayTrips.map((dt) => (
                  <div key={dt.id} style={{ display: 'flex', alignItems: 'center', gap: 11, background: '#fff', border: '1px solid #e7e2d9', borderRadius: 12, padding: '10px 12px', marginBottom: 8 }}>
                    <span style={{ fontSize: 22, flexShrink: 0 }}>{dt.icon || '🚗'}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 13, color: B.night }}>{dt.title}</div>
                      {dt.travelTime && <div style={{ fontSize: 11, color: B.muted, marginTop: 1 }}>{dt.travelTime}</div>}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, flexShrink: 0 }}>
                      {dt.plannable && dt.cityId != null ? (
                        <button
                          onClick={() => planDayTrip(dt)}
                          style={{ background: B.forest, color: '#fff', border: 'none', borderRadius: 9, padding: '7px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: FONT, whiteSpace: 'nowrap' }}
                        >
                          Plan this day
                        </button>
                      ) : !dt.plannable ? (
                        <button
                          disabled
                          style={{ background: '#fff', border: '1.5px solid #e7e2d9', color: B.muted, borderRadius: 9, padding: '7px 12px', fontSize: 12, fontWeight: 700, opacity: 0.45, cursor: 'not-allowed', fontFamily: FONT, whiteSpace: 'nowrap' }}
                        >
                          No plan data
                        </button>
                      ) : null}
                      <button
                        aria-label={`Just add ${dt.title}`}
                        onClick={() => justAddDayTrip(dt)}
                        style={{ background: '#fff', border: '1.5px solid #e7e2d9', color: B.night, borderRadius: 9, padding: '7px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: FONT, whiteSpace: 'nowrap' }}
                      >
                        Just add
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

          </div>
        </div>
      )}
    </div>
  );
}
